import React from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useSelector } from 'react-redux';
import { Popconfirm, Table, Tag, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { editSubscriptionAtom, emailsToUnsubscribeAtom } from '../atoms';
import { EmailSubscription, MetricConfig, AutoInsightConfig } from '../types';
import { useFetchEmailSubscriptionList } from './hooks';
import { EmailTitleWrapper, StyledButton, TableContainer } from './styled';
import { useUnsubscribeEmail } from '../hooks';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import Search from '@src/components/common/Table/Components/Search';
import { Retry } from '@src/components/common';
import Highlight from '@src/components/common/Highlight';
import { isAdminRoleSelector, selectedUserNameSelector } from '@src/store/common/selectors';
import { getMetricDisplayName } from '@src/utils/metrics';
import apptracker from '@src/utils/sensor/apptracker';
import { UNSUBSCRIBE_EMAIL_SUBSCRIPTIONS } from '@src/utils/sensor/track';
import { AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP } from '@src/components/notification-channel/utils';
import { isEiModule } from '@utils/constants';

const EmailSubscriptionsList = ({ isDrawerOpen }: { isDrawerOpen: boolean }) => {
  const username = useSelector(selectedUserNameSelector);
  const setEditSubscription = useSetAtom(editSubscriptionAtom);
  const { mutate: unsubscribeEmail } = useUnsubscribeEmail();
  const isAdminUser = useSelector(isAdminRoleSelector);
  const [selectedRowKeys, setSelectedRowKeys] = useAtom(emailsToUnsubscribeAtom);

  const {
    data: emailSubscriptionsList = [],
    isLoading,
    isError,
    refetch,
    onSearch,
    searchText,
  } = useFetchEmailSubscriptionList(isDrawerOpen);

  const handleEdit = (emailId: string) => {
    setEditSubscription({ emailId });
  };

  const handleUnsubscribe = async (emailId: string) => {
    await unsubscribeEmail([emailId]);
    apptracker(UNSUBSCRIBE_EMAIL_SUBSCRIPTIONS, { emailIds: [emailId] });
  };

  const onRowSelectionChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  let columns: ColumnsType<EmailSubscription> = [
    {
      title: (
        <EmailTitleWrapper>
          Email
          <Search onSearch={onSearch} searchText={searchText} />
        </EmailTitleWrapper>
      ),
      dataIndex: 'email',
      key: 'email',
      width: '30%',
      sorter: (a, b) => String(a.email).localeCompare(String(b.email)),
      render(text) {
        return searchText ? <Highlight text={text} highlight={searchText} /> : text;
      },
    },
    {
      title: 'Metrics',
      dataIndex: 'metrics',
      key: 'metrics',
      width: isEiModule ? '50%' : '60%',
      render(_k, row) {
        return (row.metrics || []).map((m: MetricConfig) => (
          <Tag style={{ margin: '5px 5px 0 0' }} key={m.metric_key}>
            {getMetricDisplayName({ metricKey: m.metric_key })}
          </Tag>
        ));
      },
    },
    {
      title: 'Auto Insights',
      dataIndex: 'autoInsights',
      key: 'autoInsights',
      width: '10%',
      render(_k, row) {
        return (row.autoInsights || []).map((autoInsightItem: AutoInsightConfig, index) => (
          <Tag style={{ margin: '5px 5px 0 0' }} key={index}>
            {AUTO_INSIGHT_NOTIFICATION_ITEMS_MAP.find((item) => item.name === autoInsightItem.type)
              ?.displayName || autoInsightItem.type}
          </Tag>
        ));
      },
    },
    {
      key: 'action',
      align: 'center',
      width: '10%',
      render(_k, row) {
        return (
          <>
            {isAdminUser ? (
              <Tooltip title="Edit">
                <StyledButton
                  type="link"
                  icon={<EditOutlined />}
                  onClick={() => row.email && handleEdit(row.email || '')}
                />
              </Tooltip>
            ) : (
              <Tooltip title="View">
                <StyledButton
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => row.email && handleEdit(row.email)}
                />
              </Tooltip>
            )}
            <Tooltip title="Unsubscribe">
              <Popconfirm
                key="delete"
                placement="left"
                title="Unsubscribe Confirmation"
                description="Do you want to unsubscribe this email from alert notifications?"
                icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                okText="Unsubscribe"
                cancelText="Cancel"
                onConfirm={() => row.email && handleUnsubscribe(row.email || '')}
                disabled={!(isAdminUser || row.email === username)}
              >
                <StyledButton
                  disabled={!(isAdminUser || row.email === username)}
                  type="link"
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Tooltip>
          </>
        );
      },
    },
  ];

  // if the current product is not Video, we need remove the Auto Insights column
  if (!isEiModule) {
    columns = columns.filter((column) => column.title !== 'Auto Insights');
  }

  return (
    <TableContainer>
      <Retry isLoading={isLoading} fetchData={refetch} showError={isError}>
        <Table
          sticky
          dataSource={emailSubscriptionsList}
          columns={columns}
          rowKey={(es) => es.email || ''}
          showSorterTooltip={false}
          scroll={{ x: true }}
          pagination={false}
          rowSelection={
            isAdminUser ? { selectedRowKeys, onChange: onRowSelectionChange } : undefined
          }
        />
      </Retry>
    </TableContainer>
  );
};

export default EmailSubscriptionsList;
